import { useState } from 'react'
import { ICON_GROUPS, PILL_COLORS } from '../../../utils/iconDescriptions'
import RatingIcon from '../../shared/RatingIcon'

/**
 * Modal multi-select de iconos para filtros include/exclude IC.
 * Duplica el layout de IconSelectorModal (ICON_GROUPS + RatingIcon)
 * pero con multi-select inline (ring verde/rojo segun mode) sin auto-close.
 *
 * @param {'include'|'exclude'} mode
 * @param {string[]} initialSelected - icon names ya seleccionados
 * @param {Function} onConfirm - (names: string[]) => void
 * @param {Function} onClose - () => void
 */
export default function IncludeExcludeIconModal({ mode, initialSelected, onConfirm, onClose }) {
  const [selected, setSelected] = useState(initialSelected || [])

  const isInclude = mode === 'include'
  const headerBg = isInclude
    ? 'bg-emerald-50 dark:bg-emerald-900/30 border-emerald-300 dark:border-emerald-700'
    : 'bg-rose-50 dark:bg-rose-900/30 border-rose-300 dark:border-rose-700'
  const headerText = isInclude
    ? 'text-emerald-800 dark:text-emerald-200'
    : 'text-rose-800 dark:text-rose-200'
  const title = isInclude ? '⭐ Incluir IC' : '⭐ Excluir IC'
  const subtitle = isInclude
    ? 'Los contactos deben tener al menos uno de estos íconos para aparecer'
    : 'Los contactos con cualquiera de estos íconos NO aparecerán'
  const selectedBorder = isInclude
    ? 'border-emerald-500 dark:border-emerald-400 ring-2 ring-emerald-300 dark:ring-emerald-600'
    : 'border-rose-500 dark:border-rose-400 ring-2 ring-rose-300 dark:ring-rose-600'
  const selectedBg = isInclude
    ? 'bg-emerald-50 dark:bg-emerald-900/30'
    : 'bg-rose-50 dark:bg-rose-900/30'

  const toggle = (iconName) => {
    setSelected(prev =>
      prev.includes(iconName) ? prev.filter(n => n !== iconName) : [...prev, iconName]
    )
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-[675px] mx-4 max-h-[90vh] flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`p-4 border-b rounded-t-2xl ${headerBg}`}>
          <h3 className={`text-base font-semibold ${headerText}`}>{title}</h3>
          <p className={`text-xs mt-1 ${headerText} opacity-80`}>{subtitle}</p>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-3 space-y-1">
          {ICON_GROUPS.map(group => {
            const colors = group.color ? (PILL_COLORS[group.color] || null) : null
            const icons = (
              <div className="flex gap-2 justify-start flex-wrap">
                {group.icons.map(iconName => {
                  const isSel = selected.includes(iconName)
                  return (
                    <button
                      key={iconName}
                      onClick={() => toggle(iconName)}
                      type="button"
                      title={iconName}
                      className={`
                        p-1.5 rounded-xl border-2 transition-all duration-150 transform hover:scale-105 active:scale-95
                        ${isSel
                          ? `${selectedBorder} ${selectedBg}`
                          : 'bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-600 hover:border-gray-400 dark:hover:border-gray-400'
                        }
                      `}
                    >
                      <RatingIcon iconId={iconName} size="lg" />
                    </button>
                  )
                })}
              </div>
            )

            if (!group.label || !colors) {
              return (
                <div key={group.key}>
                  {icons}
                </div>
              )
            }

            return (
              <div key={group.key} className={`rounded-2xl border px-3 py-0.5 ${colors.container}`}>
                <div className={`text-[10px] font-bold uppercase tracking-wider mb-0.5 px-1 ${colors.label}`}>
                  {group.label}
                </div>
                {icons}
              </div>
            )
          })}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center px-6 py-3 border-t border-gray-200 dark:border-gray-700">
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {selected.length} seleccionado{selected.length !== 1 ? 's' : ''}
          </span>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              type="button"
              className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition font-medium"
            >
              Cancelar
            </button>
            <button
              onClick={() => { onConfirm(selected); onClose() }}
              type="button"
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition font-medium"
            >
              Confirmar
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
